import { useState } from "react";
import { toast } from "sonner";
import { Sparkles } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { VoiceRecorder } from "@/components/VoiceRecorder";
import { api, errText } from "@/lib/api";

export const PostItVoiceDialog = ({ open, onOpenChange, onCreated }) => {
  const [busy, setBusy] = useState(false);

  const send = async (file) => {
    setBusy(true);
    try {
      const body = new FormData();
      body.append("file", file);
      const { data } = await api.post("/postits/voice", body);
      if (data?.pending_voice) {
        toast.success("Recording saved on this device", {
          description: "It will be written up as soon as you're back online.",
          duration: 8000,
        });
        onOpenChange(false);
        onCreated?.(data);
        return;
      }
      toast.success("Dictation transcribed and tidied up");
      onOpenChange(false);
      onCreated?.(data);
    } catch (e) {
      toast.error(errText(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-sm max-w-xl max-h-[75vh] overflow-y-auto" data-testid="postit-voice-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-2xl">Dictate a note</DialogTitle>
          <DialogDescription className="text-sm">
            Speak in English, Hindi or a mix. Every word you say is kept — punctuation and grammar
            are cleaned up, a short heading is added, and the recording stays on the note.
          </DialogDescription>
        </DialogHeader>

        <div className="py-3 space-y-4">
          <VoiceRecorder onAudio={send} busy={busy} />
          {busy ? (
            <p className="inline-flex items-center gap-2 text-sm text-muted-foreground">
              <Sparkles className="h-4 w-4 animate-pulse" /> Transcribing and proofreading…
            </p>
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};
